import { type ApolloClient, type NormalizedCacheObject } from '@apollo/client'

import { DEFAULT_USER_LOCATION, RADIUS_SLIDER_HALF_POINT_KMS } from '@/constants/location'
import { convertGlobalToCookieLocation } from '@/domain/location/convertGlobalToCookieLocation'
import { getInitialLocation } from '@/domain/location/getInitialLocation'
import { setLastRadiusLocalStorage } from '@/domain/location/setLastRadiusLocalStorage'
import { setModifiedRadiusLocalStorage } from '@/domain/location/setModifiedRadiusLocalStorage'
import { type UserLocation, GetUserLocationDocument } from '@/generated/graphql-types'
import { initializeApollo } from '@/lib/apollo/apolloClient'
import { type GlobalLocation, searchLocationVar } from '@/lib/apollo/locationCache'

import { updateLatestSearchCookie } from '../cookies/latestSearchCookie'

export type SearchLocation = {
  /** Defines locationID */
  id?: string
  /**
   * Defines if maps is rendered with radius or points to an exact location
   */
  isRegion?: boolean
  /**
   * Defines initial selected latitude
   */
  latitude: number | null
  /**
   * Defines initial selected longitude
   */
  longitude: number | null
  /**
   * Defines initial search radius
   */
  radius: number
  /**
   * Defines initial street address
   */
  streetAddress?: string
}

const convertGlobalToUserLocation = (globalLocation: GlobalLocation): UserLocation => {
  if (!globalLocation.id) return DEFAULT_USER_LOCATION

  const location: UserLocation = {
    id: globalLocation.id,
    isRegion: !!globalLocation.isRegion,
    name: globalLocation.name || { en_CA: '', fr_CA: '' },
    area: null,
  }

  if (globalLocation.isRegion || !globalLocation.latitude || !globalLocation.longitude) {
    return location
  }

  location.area = {
    address: globalLocation.streetAddress || null,
    latitude: globalLocation.latitude,
    longitude: globalLocation.longitude,
    radius: globalLocation.radius ?? RADIUS_SLIDER_HALF_POINT_KMS,
  }

  return location
}

/**
 * LOCATION ID BASED ON APOLLO REACTIVE VARIABLES
 * https://www.apollographql.com/docs/react/local-state/reactive-variables/
 * */
export const setGlobalLocation = (
  location: GlobalLocation,
  automaticallySet?: boolean,
  preventSetCache?: boolean
) => {
  const legacyCookieLocation = convertGlobalToCookieLocation(location)

  /** Set reactive variable */
  searchLocationVar(location)

  /** Update location in cache */
  /**
   * It should not set the cache on the first load to Canada.
   * This will prevent the location from being incorrectly updated on the first load
   * */
  if (!preventSetCache) {
    updateUserLocationCache(convertGlobalToUserLocation(location))
  }

  /** Update cookies */
  updateLatestSearchCookie(legacyCookieLocation)

  /** Should reset local storage value if user change location to a region*/
  location.isRegion && setModifiedRadiusLocalStorage(false)

  /** Update local storage with last radius value if location was not automatically set */
  !automaticallySet && setLastRadiusLocalStorage(location.radius)
}

/**
 * Set initial location data from latest search cookie,
 * then save to reactive var in apollo cache
 */
export const setInitialLocation = async () => {
  const location = await getInitialLocation()
  setGlobalLocation(location, undefined, true)
}

export const updateUserLocationCache = (
  location: UserLocation,
  client?: ApolloClient<NormalizedCacheObject>
) => {
  if (!client) {
    client = initializeApollo()
  }

  client.cache.writeQuery({
    query: GetUserLocationDocument,
    data: { userLocation: location },
  })
}
